/**
 * Error Boundary with API Component
 * 
 * Error boundary component that catches React errors and reports to backend:
 * - Error catching and display
 * - Backend error reporting
 * - Error recovery options
 * - User-friendly error messages
 * 
 * NOTE: This component intentionally does NOT use i18n translations.
 * Error boundaries must be resilient to initialization failures since they
 * are exactly what runs when other parts of the app (including i18n) fail.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Function} [props.onReset] - Callback when error is reset
 * @returns {JSX.Element} The error boundary with API component
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Stack
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon
} from '@mui/icons-material';

class ErrorBoundaryWithAPI extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    
    // Log full error details to console for debugging
    console.error('ErrorBoundary caught error:', error);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Send error to backend API
    this.reportErrorToBackend(error, errorInfo);
  }

  reportErrorToBackend = async (error, errorInfo) => {
    try {
      const response = await fetch('/api/v1/errors/frontend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`
        },
        body: JSON.stringify({
          error: {
            message: error.toString(),
            stack: error.stack
          },
          errorInfo: {
            componentStack: errorInfo.componentStack
          },
          context: {
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to report error: ${response.status}`);
      }
    } catch (reportError) {
      // Log to console only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to report error to backend:', reportError);
      }
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
              
              <Typography variant="h5" align="center">
                An error occurred
              </Typography>

              <Alert severity="error" sx={{ width: '100%' }}>
                <AlertTitle>Error Details</AlertTitle>
                <Typography variant="body2">
                  {this.state.error && this.state.error.toString()}
                </Typography>
              </Alert>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <Box 
                  sx={{ 
                    width: '100%', 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem'
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" align="center">
                This error has been automatically reported to our technical team.
              </Typography>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={() => window.location.href = '/'}
                >
                  Back to Home
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReset}
                >
                  Refresh
                </Button>
              </Stack>
            </Box>
          </Paper>
          </Box>
      );
    }

    return this.props.children;
  }
}

export default React.memo(ErrorBoundaryWithAPI);

